import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { cn } from '@/lib/utils';
import { AlertTriangle } from 'lucide-react';

interface LineChartProps {
  data: any[];
  title: string;
  description?: string;
  className?: string;
  hasError?: boolean;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-background/95 p-3 border border-border/50 shadow-lg rounded-lg backdrop-blur-sm">
        <p className="text-sm font-medium text-foreground">{`${label}`}</p>
        <p className="text-sm font-semibold text-primary">{`${payload[0].value}`}</p>
      </div>
    );
  }

  return null;
};

const LineChartComponent = ({ data, title, description, className, hasError }: LineChartProps) => {
  return (
    <div className={cn("card-glass rounded-xl p-5", className)}>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">{title}</h3>
          {description && <p className="text-sm text-muted-foreground mt-1">{description}</p>}
        </div>
        {hasError && (
          <div className="flex items-center text-yellow-500" title="Using fallback data">
            <AlertTriangle className="h-4 w-4 mr-1" />
            <span className="text-xs">Fallback Data</span>
          </div>
        )}
      </div>
      
      <div className="h-72 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={data}
            margin={{
              top: 10,
              right: 10,
              left: 0,
              bottom: 0,
            }}
          >
            <defs>
              <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={hasError ? "hsl(var(--warning))" : "hsl(var(--primary))"} stopOpacity={0.8} />
                <stop offset="100%" stopColor={hasError ? "hsl(var(--warning))" : "hsl(var(--primary))"} stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" opacity={0.3} />
            <XAxis 
              dataKey="name" 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
              dy={10}
            />
            <YAxis 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
              dx={-10}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area 
              type="monotone" 
              dataKey="value" 
              stroke={hasError ? "hsl(var(--warning))" : "hsl(var(--primary))"} 
              strokeWidth={2}
              fillOpacity={1} 
              fill="url(#colorValue)"
              className="transition-all duration-300 hover:opacity-80 drop-shadow-sm"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default LineChartComponent;
